import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ScanProgressUpdate {
  ladingBill: string;
  scannedCartons: number;
  totalCartons: number;
  percentage: number;
  cartonNumber: string;
  scannedAt: Date;
  scannedBy: string;
}

@Injectable({
  providedIn: 'root'
})
export class ScanProgressSignalRService {
  private hubConnection: signalR.HubConnection | null = null;
  private scanProgressSubject = new BehaviorSubject<ScanProgressUpdate | null>(null);
  public scanProgress$: Observable<ScanProgressUpdate | null> = this.scanProgressSubject.asObservable();

  private connectionStateSubject = new BehaviorSubject<signalR.HubConnectionState>(
    signalR.HubConnectionState.Disconnected
  );
  public connectionState$ = this.connectionStateSubject.asObservable();

  constructor() {}

  public async startConnection(authToken: string): Promise<void> {
    // Use environment API URL, fallback to production for deployed apps
    const apiBaseUrl = environment.apiUrl.replace('/api', ''); // Remove /api suffix

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${apiBaseUrl}/hubs/scan-progress`, {
        accessTokenFactory: () => authToken,
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.ServerSentEvents
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Information)
      .build();

    // Handle reconnection events
    this.hubConnection.onreconnecting(() => {
      console.log('ScanProgress SignalR reconnecting...');
      this.connectionStateSubject.next(signalR.HubConnectionState.Reconnecting);
    });

    this.hubConnection.onreconnected(() => {
      console.log('ScanProgress SignalR reconnected');
      this.connectionStateSubject.next(signalR.HubConnectionState.Connected);
    });

    this.hubConnection.onclose(() => {
      console.log('ScanProgress SignalR connection closed');
      this.connectionStateSubject.next(signalR.HubConnectionState.Disconnected);
    });

    // Listen for scan progress updates
    this.hubConnection.on('ScanProgressUpdated', (update: ScanProgressUpdate) => {
      console.log('Received scan progress update:', update);
      // Ensure dates are properly converted
      const processedUpdate = {
        ...update,
        scannedAt: new Date(update.scannedAt)
      };
      this.scanProgressSubject.next(processedUpdate);
    });

    try {
      await this.hubConnection.start();
      console.log('ScanProgress SignalR connection established');
      this.connectionStateSubject.next(signalR.HubConnectionState.Connected);
    } catch (err) {
      console.error('Error establishing ScanProgress SignalR connection:', err);
      throw err;
    }
  }

  public async joinPreAlertRoom(ladingBill: string): Promise<void> {
    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      await this.hubConnection.invoke('JoinPreAlertRoom', ladingBill);
      console.log(`Joined pre-alert room: ${ladingBill}`);
    }
  }

  public async leavePreAlertRoom(ladingBill: string): Promise<void> {
    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      await this.hubConnection.invoke('LeavePreAlertRoom', ladingBill);
      console.log(`Left pre-alert room: ${ladingBill}`);
    }
  }

  public async stopConnection(): Promise<void> {
    if (this.hubConnection) {
      await this.hubConnection.stop();
      this.connectionStateSubject.next(signalR.HubConnectionState.Disconnected);
    }
  }

  public isConnected(): boolean {
    return this.hubConnection?.state === signalR.HubConnectionState.Connected;
  }

  public getConnectionState(): signalR.HubConnectionState {
    return this.hubConnection?.state || signalR.HubConnectionState.Disconnected;
  }

  // === PHASE 5: DUPLICATE SCAN EVENT LISTENER ===

  public onDuplicateScan(callback: (data: any) => void): void {
    if (this.hubConnection) {
      this.hubConnection.on('ScanEvent', (data: any) => {
        if (data.eventType === 'SCAN_DUPLICATE') {
          callback(data);
        }
      });
    }
  }
}